import AddCTA, { CTAToBeEditedInterface } from 'Components/campaigns/collaborations/milestones/AddCTA';
import ListEditor from 'Components/campaigns/collaborations/milestones/ListEditor';
import TalkingPoints from 'Components/campaigns/collaborations/milestones/TalkingPoints';
import CTA from 'Components/campaigns/create-edit/CTA';
import ScreeningQuestion from 'Components/campaigns/create-edit/ScreeningQuestion';
import CreatableTags from 'Components/Common/CreatableTags';
import CustomMultiSelect from 'Components/Common/CustomMultiSelect';
import RichTextEditor from 'Components/Common/RichTextEditor';
import CounterInput from 'Components/input/CounterInput/CounterInput';
import RadioTileGroup from 'Components/input/RadioTileGroup/RadioTileGroup';
import RichListEditor from 'Components/input/RichListEditor/RichListEditor';
import { PRIVILEGE } from 'constants/privilege';
import { usePrivileges } from 'hooks/useAuth';
import CampaignCreationLayout from 'Layouts/CampaignCreationLayout'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Col, Modal, ModalBody, Row, Spinner } from 'reactstrap';
import { fetchCampaignDetails } from 'slices/campaigns/details/thunk';
import { fetchCampaignRequirement } from 'slices/campaigns/requirements/thunk';
import { RootState, AppDispatch } from 'store';
import Select from 'react-select'
import { Collapse } from 'react-collapse';
import { useDropzone } from 'react-dropzone';
import { addCampaignGalleryImage, deleteCampaignGalleryImage, updateCampaignMilestone } from 'services/api/campaign';
import { toast } from 'react-toastify';
import { CampaignFile } from 'types/campaign/collaborations';
import { getMediaTypeFromURL, safeTextFromHTML, scrollToElement } from 'utils/helpers';
import CloseButton from 'Components/Common/CloseButton';
import MilestoneCaption from 'Components/campaigns/collaborations/milestone/components/MilestoneCaption';
import MilestoneScript from 'Components/campaigns/collaborations/milestone/components/MilestoneScript';
import withAccessControl from 'hoc/withAccessControl';
import CampaignCTAs from 'Components/campaigns/create-edit/CampaignCTAs';
import FormField from 'Components/Common/FormField';

const CampaignDeliverables: React.FC<{ viewOnly?: boolean }> = ({ viewOnly }) => {

    const { campaignId } = useParams();

    const campaignDetails = useSelector((state: RootState) => state.campaignDetails.campaigns[(campaignId ?? 'create')])

    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const milestonesData = useSelector((state: RootState) => state.campaignMilestones.items[campaignId!]?.milestonesData);
    const campaignMilestones = milestonesData?.milestones ?? [];
    const gigTypes = milestonesData?.gig_types ?? [];
    const properties = milestonesData?.properties ?? {};
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState<boolean>(false);
    const privileges = usePrivileges();
    const campaignGigs = useSelector((state: RootState) => state.campaignMilestones.gigs[campaignId!]?.items || []);
    const campaignMilestone = (Array.isArray(campaignMilestones) && campaignMilestones.length > 0) ? campaignMilestones[0] : null;

    const [milestone, setMilestone] = useState(campaignMilestone ?? null);
    const [advanceOptions, showAdvanceOptions] = useState<boolean>(false);
    const [selectedMedia, setSelectedMedia] = useState<any>(null);

    const milestoneGigType = (gigTypes ?? []).find((gigType) => gigType.id && milestone?.gig_type_id && gigType.id === milestone.gig_type_id);

    const [sampleScriptsAllowed, allowSampleScripts] = useState(false);
    const [sampleCaptionsAllowed, allowSampleCaptions] = useState(false);
    const [searchParams] = useSearchParams();
    const validate = searchParams.get('validate');
    const [validatedOnce, setValidatedOnce] = useState(false);

    useEffect(() => {
        setMilestone(campaignMilestone ?? null);
    }, [campaignMilestone])

    useEffect(() => {
        if (campaignId) {
            dispatch(fetchCampaignRequirement(campaignId))
            dispatch(fetchCampaignDetails(campaignId))
        }
    }, [dispatch, campaignId])

    const handleMilestoneChange = (key: string, value: any) => {
        if (!milestone) return;
        setMilestone({ ...milestone, [key]: value })
    }

    const canSetDMAutomation = useMemo(() => {
        if (milestone?.specifications?.cta && Array.isArray(milestone.specifications.cta) && milestone.specifications.cta.length > 0) {
            return milestone.specifications.cta.some((cta) => cta.url);
        }
        return false;
    }, [milestone])

    useEffect(() => {
        if (validate === 'true' && !validatedOnce && milestone) {
            validateMilestoneData();
            setValidatedOnce(true);
        }
    }, [validate, milestone, validatedOnce])


    const onSamplePostsDrop = useCallback(async (acceptedFiles: File[]) => {
        if (!milestone || !campaignId || !selectedAgencyId || viewOnly) return;
        for (let file of acceptedFiles) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', 'sample_post');
            formData.append("milestone_id", String(milestone?.id));
            try {
                const uploadedFile = await addCampaignGalleryImage(selectedAgencyId, campaignId, formData);
                if (uploadedFile.success) {
                    const campaignFiles = Array.isArray(milestone?.campaign_files) ? [...milestone.campaign_files] : [];
                    campaignFiles.push({ filename: uploadedFile.name, uuid: uploadedFile.uuid, url: uploadedFile.url, type: 'sample_post' });
                    handleMilestoneChange('campaign_files', campaignFiles);
                }
            } catch (error: any) {
                console.log(error);
                toast.error(error?.response?.data?.message ?? 'Failed to upload file, please try again!')
            }
        }

    }, [milestone, selectedAgencyId, campaignId]);

    const { getRootProps: getRootPropsForSamplePosts, getInputProps: getInputPropsForSamplePost } = useDropzone({
        onDrop: onSamplePostsDrop,
        accept: { ".png, .jpg, .jpeg, .mp4, .heic": [] },
        multiple: true,
    });

    const getSamplePosts = (): CampaignFile[] => {
        if (milestone && Array.isArray(milestone.campaign_files)) {
            return milestone.campaign_files.filter((cf) => cf.type === 'sample_post');
        }

        return [];
    }

    const deleteMilestoneFile = async (uuid: string) => {
        if (!selectedAgencyId || !campaignId || viewOnly) return;
        try {
            await deleteCampaignGalleryImage(selectedAgencyId, campaignId, uuid);
            const campaignFiles = Array.isArray(milestone?.campaign_files) ? [...milestone.campaign_files.filter((cf) => cf.uuid !== uuid)] : [];
            handleMilestoneChange('campaign_files', campaignFiles);
        } catch (error: any) {
            toast.error(error?.response?.data?.message ?? 'Failed to delete the milestone file')
        }
    }

    useEffect(() => {
        if (milestone && Array.isArray(milestone.specifications?.sample_captions) && milestone.specifications.sample_captions.length > 0) {
            allowSampleCaptions(true);
        } else {
            allowSampleCaptions(false);
        }
        if (milestone && Array.isArray(milestone.specifications?.sample_scripts) && milestone.specifications.sample_scripts.length > 0) {
            allowSampleScripts(true);
        } else {
            allowSampleScripts(false);
        }
    }, [milestone])

    const validateMilestoneData = () => {
        if (!milestone) return false;

        setErrors({});

        const validation_errors: Record<string, string> = {};
        if (!milestone?.specifications?.quick_repost && (!milestone.brief || !safeTextFromHTML(milestone.brief))) {
            validation_errors.brief = "Milestone brief is required!";
        }

        if (campaignDetails?.campaign?.platform === 'ig') {
            if (milestone.content_media_types.REELS && (isNaN(Number(milestone.content_media_types.REELS)) || Number(milestone.content_media_types.REELS) < 0) || Number(milestone.content_media_types.REELS) > 1) {
                validation_errors.content_media_types = "No of reels can only be 1 or 0";
            }
            if (milestone.content_media_types.FEED && (isNaN(Number(milestone.content_media_types.FEED)) || Number(milestone.content_media_types.FEED) < 0) || Number(milestone.content_media_types.FEED) > 1) {
                if (!validation_errors.content_media_type) validation_errors.content_media_types = '';
                validation_errors.content_media_types = 'Max 1 post is allowed.'
            }
            if (milestone.content_media_types.STORY && (isNaN(Number(milestone.content_media_types.STORY)) || Number(milestone.content_media_types.STORY) < 0) || Number(milestone.content_media_types.STORY) > 2) {
                if (!validation_errors.content_media_type) validation_errors.content_media_types = '';
                validation_errors.content_media_types += 'Max 2 stories are allowed.'
            }

            if (!milestone?.specifications?.quick_repost && (isNaN(Number(milestone?.content_media_types?.REELS)) || Number(milestone?.content_media_types?.REELS) === 0) && (isNaN(Number(milestone?.content_media_types?.FEED)) || Number(milestone?.content_media_types?.FEED) === 0) && (isNaN(Number(milestone?.content_media_types?.STORY)) || Number(milestone?.content_media_types?.STORY) === 0)) {
                validation_errors.content_media_types = "Atleast one content media type is required."
            }
        }
        else if (campaignDetails?.campaign?.platform === 'yt') {
            if (isNaN(Number(milestone?.content_media_types?.VIDEO)) || Number(milestone?.content_media_types?.VIDEO) === 0 || Number(milestone.content_media_types.VIDEO) > 1) {
                validation_errors.content_media_types = "No of videos must be 1.";
            }
        }

        if (!milestone?.specifications?.quick_repost && (!Array.isArray(milestone.specifications?.cta) || milestone.specifications.cta.filter((c) => c.text).length < 1)) {
            validation_errors.cta = 'Please add atleast one CTA.'
        }

        if (milestone.specifications.dm_automation_required) {
            if (!milestone.specifications?.dm_automation_triggers || !Array.isArray(milestone.specifications.dm_automation_triggers) || milestone.specifications.dm_automation_triggers.length === 0) {
                validation_errors.dm_automation_triggers = "Please select atleast one trigger for DM automation.";
            }

            if (!milestone.specifications?.dm_automation_button_text || milestone.specifications.dm_automation_button_text.trim() === '') {
                validation_errors.dm_automation_button_text = "Please select button text for DM automation.";
            }

        }

        if (Array.isArray(milestone.specifications?.talking_points)) {
            for (let talkingPoint of milestone.specifications.talking_points) {
                if (!talkingPoint?.title || !Array.isArray(talkingPoint.pointers) || talkingPoint.pointers.length === 0) {
                    validation_errors.talking_points = "Incorrect/Incomplete talking points format!";
                }
            }
        }

        if (!milestone?.specifications?.quick_repost && campaignDetails?.campaign?.goal === 'conversion' && !milestone.specifications.ad_rights_duration) {
            validation_errors.ad_rights_duration = "Please select Ad rights duration"
        }
        if (!milestone?.specifications?.quick_repost && !milestone.specifications.content_rights_duration) {
            validation_errors.content_rights_duration = "Please select content rights duration"
        }

        console.log(validation_errors);

        setErrors(validation_errors);

        return !(Object.values(validation_errors).length > 0);

    }

    useEffect(() => {
        if (errors && Object.keys(errors).length > 0) {
            const invalidFieldKey = Object.keys(errors)[0];
            scrollToElement(`error-${invalidFieldKey}`);
        }
    }, [errors])

    const submitDeliverables = async () => {
        if (!campaignId || !milestone?.id || !selectedAgencyId || !validateMilestoneData() || viewOnly) return;

        setLoading(true);
        try {
            await updateCampaignMilestone(selectedAgencyId, campaignId, String(milestone.id), milestone);
            navigate('/campaign/' + campaignId + '/controls');
        } catch (error) {
            toast.error('Failed to update milestone');
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <CampaignCreationLayout
                activeStep='deliverables'
                viewOnly={viewOnly}
                actionRow={
                    <div className="d-flex justify-content-between mb-4">
                        <div></div>
                        <button type='button' onClick={() => submitDeliverables()} disabled={loading || !privileges.includes(PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)} className="theme-btn theme-btn-lg btn-gradient form-continue-btn d-flex justify-content-between align-items-center">
                            <React.Fragment>
                                {
                                    'Proceed to Next'
                                }
                                {
                                    loading ? <Spinner size="sm" className='me-2'> Loading... </Spinner> : <i className='bx fs-4 bx-right-arrow-alt' ></i>
                                }
                            </React.Fragment>
                        </button>
                    </div>
                }
            >
                {
                    milestone &&
                    <div className='campaign-deliverables'>
                        {
                            milestoneGigType?.slug === 'repost' && campaignDetails?.campaign?.platform === 'ig' &&
                            <>
                                <div className="form-group">
                                    <label htmlFor="aitcp" className="fs-14px mb-2">{properties?.quick_repost_3?.label}</label>
                                    <FormField
                                        showLabel={false}
                                        property={{ ...properties?.quick_repost_3, label: '' }}
                                        value={!!milestone?.specifications.quick_repost}
                                        onChange={(value) => {
                                            const specifications = { ...milestone.specifications, quick_repost: value, advertiser_permission_required: value ? false : campaignDetails?.campaign?.goal === 'conversion' ? true : false };
                                            handleMilestoneChange('specifications', specifications);
                                        }}
                                    />
                                </div>
                                {
                                    !(milestone?.specifications?.quick_repost) &&
                                    <div className="divider my-5"></div>
                                }
                            </>
                        }
                        {
                            !(milestone?.specifications?.quick_repost) &&
                            <>


                                <div className="form-group">
                                    <label htmlFor="media-count" className='fs-16px'>Number of {campaignDetails?.campaign?.platform === 'yt' ? 'Videos' : 'Reels/Post/Story'} <span className="text-danger">*</span></label>
                                    <span className='field-desc'>How many {campaignDetails?.campaign?.platform === 'yt' ? 'videos' : 'reels/stories/posts'} should each creator produce for you?</span>
                                    <Row>
                                        {
                                            campaignDetails?.campaign?.platform === 'yt' &&
                                            <Col sm={4}>
                                                <div className="form-group">
                                                    <label htmlFor="reels-count" className='fs-12px'>Videos</label>
                                                    <CounterInput disabled={viewOnly} value={milestone.content_media_types.VIDEO ?? 0} onChange={(val) => handleMilestoneChange('content_media_types', { ...milestone.content_media_types, VIDEO: val ?? 0 })} />
                                                </div>
                                            </Col>
                                        }
                                        {
                                            campaignDetails?.campaign?.platform === 'ig' &&
                                            <>
                                                <Col sm={4}>
                                                    <div className="form-group">
                                                        <label htmlFor="reels-count" className='fs-12px'>Reels</label>
                                                        <CounterInput disabled={viewOnly} value={milestone.content_media_types.REELS ?? 0} onChange={(val) => handleMilestoneChange('content_media_types', { ...milestone.content_media_types, REELS: val ?? 0 })} />
                                                    </div>
                                                </Col>
                                                <Col sm={4}>
                                                    <div className="form-group">
                                                        <label htmlFor="reels-count" className='fs-12px'>Post</label>
                                                        <CounterInput disabled={viewOnly} value={milestone.content_media_types.FEED ?? 0} onChange={(val) => handleMilestoneChange('content_media_types', { ...milestone.content_media_types, FEED: val ?? 0 })} />
                                                    </div>
                                                </Col>
                                                <Col sm={4}>
                                                    <div className="form-group">
                                                        <label htmlFor="reels-count" className='fs-12px'>Story</label>
                                                        <CounterInput disabled={viewOnly} value={milestone.content_media_types.STORY ?? 0} onChange={(val) => handleMilestoneChange('content_media_types', { ...milestone.content_media_types, STORY: val ?? 0 })} />
                                                    </div>
                                                </Col>
                                            </>
                                        }
                                    </Row>
                                    {
                                        errors.content_media_types &&
                                        <span className="text-danger d-block" id='error-content_media_types'>{errors.content_media_types}</span>
                                    }
                                </div>
                                <div className="divider my-5"></div>
                                {
                                    properties?.content_rights_duration &&
                                    <div className="form-group mt-3">
                                        <label htmlFor="references" className="fs-14px">{properties?.content_rights_duration?.label} <span className="text-danger">*</span> </label>
                                        <FormField disabled={viewOnly} showLabel={false} property={{ ...properties?.content_rights_duration, mandatory: true, description: 'Specify how long you can use this content' }} value={milestone?.specifications?.content_rights_duration ?? ''} onChange={(val) => {
                                            const specs = { ...milestone?.specifications };
                                            specs.content_rights_duration = val;
                                            handleMilestoneChange('specifications', specs);
                                        }} />
                                        {errors.content_rights_duration &&
                                            <span className="text-danger d-block mt-n3" id='error-content_rights_duration'>{errors.content_rights_duration}</span>
                                        }
                                    </div>
                                }
                                {
                                    campaignDetails?.campaign?.goal === 'conversion' && properties?.ad_rights_duration &&
                                    <div className="form-group mt-3">
                                        <label htmlFor="references" className="fs-14px">{properties?.ad_rights_duration?.label} <span className="text-danger">*</span> </label>
                                        <FormField disabled={viewOnly} showLabel={false} property={{ ...properties?.ad_rights_duration, mandatory: true, description: "Specify how long you want to run ads on this content" }} value={milestone?.specifications?.ad_rights_duration ?? ''} onChange={(val) => {
                                            const specs = { ...milestone?.specifications };
                                            specs.ad_rights_duration = val;
                                            handleMilestoneChange('specifications', specs);
                                        }} />
                                        {errors.ad_rights_duration &&
                                            <span className="text-danger d-block mt-n3" id='error-ad_rights_duration'>{errors.ad_rights_duration}</span>
                                        }
                                    </div>
                                }
                                <div className="form-group mt-3">
                                    <label htmlFor="references" className="fs-14px">Should the Creator add any hashtags? <span className="optional">(Optional)</span></label>
                                    <span className="field-desc">Write the hashtags that you would like the creator to add.</span>
                                    <CreatableTags
                                        placeholder="Add hashtags"
                                        disabled={viewOnly}
                                        tagType='hashtag'
                                        values={milestone?.specifications?.hashtags ?? []}
                                        onChange={(tags) => {
                                            const specifications = { ...milestone?.specifications, hashtags: tags };
                                            handleMilestoneChange('specifications', specifications);
                                        }}
                                    />
                                </div>
                                <div className="form-group mt-3">
                                    <label htmlFor="references" className="fs-14px">Should the Creator tag your Brand? <span className="optional">(Optional)</span></label>
                                    <span className="field-desc">Add your Brand handle.</span>
                                    <CreatableTags
                                        placeholder="Add handle(s)"
                                        tagType='usertag'
                                        disabled={viewOnly}
                                        values={milestone?.specifications?.mentions ?? []}
                                        onChange={(tags) => {
                                            const specifications = { ...milestone?.specifications, mentions: tags };
                                            handleMilestoneChange('specifications', specifications);
                                        }}
                                    />
                                </div>
                                <div className="form-group mt-3">
                                    <label htmlFor="references" className="fs-14px">Should the Creator collaborate with anyone? <span className="optional">(Optional)</span></label>
                                    <span className="field-desc">Add collaborator handle(s) if any.</span>
                                    <CreatableTags
                                        placeholder="Add collaborator(s)"
                                        tagType='usertag'
                                        disabled={viewOnly}
                                        values={milestone?.specifications?.collaborators ?? []}
                                        onChange={(tags) => {
                                            const specifications = { ...milestone?.specifications, collaborators: tags };
                                            handleMilestoneChange('specifications', specifications);
                                        }}
                                    />
                                </div>
                                <div className="divider my-5"></div>
                                <div className="form-group gap-3">
                                    <label htmlFor="step-by-step-guide" className='fs-16px'>Give a step-by-step guide for the creator <span className="text-danger">*</span></label>
                                    <RichTextEditor
                                        disabled={viewOnly}
                                        placeholder="Describe your campaign requirements in detail"
                                        value={milestone?.brief ?? ''}
                                        onChange={(value: string) => {
                                            if (!milestone) return;
                                            setMilestone({ ...milestone, brief: value })
                                        }}
                                    />
                                    {errors.brief &&
                                        <span className="text-danger d-block" id='error-brief'>{errors.brief}</span>
                                    }
                                </div>
                                <div className="divider my-5"></div>
                                <div className="form-group ">
                                    <label htmlFor="step-by-step-guide" className='fs-16px'>What should creators avoid?</label>
                                    <span className="field-desc mb-2">Is there anything specific that you don't want the Creator to do?</span>
                                    <RichListEditor disabled={viewOnly} value={milestone.specifications.dont} onChange={(val) => handleMilestoneChange('specifications', { ...milestone.specifications, dont: val.filter((item) => item?.trim) })} />
                                </div>
                                <div className="divider my-5"></div>
                                <div className="form-group">
                                    <label htmlFor="screening-questions" className='fs-16px'>CTA(s) <span className="text-danger">*</span></label>
                                    <span className="field-desc mb-2"> Provide call-to-actions for the creators to use in their content </span>
                                </div>
                                <div className="campaign-sub-form"><CampaignCTAs viewOnly={viewOnly} cta_list={milestone?.specifications?.cta ?? []} onChange={((ctaList) => {
                                    const specs = { ...milestone?.specifications };
                                    specs.cta = ctaList;
                                    handleMilestoneChange('specifications', specs);
                                })} />
                                    {errors.cta &&
                                        <span className="text-danger d-block" id='error-cta'>{errors.cta}</span>
                                    }
                                </div>
                                {
                                    properties.dm_automation_required && canSetDMAutomation && campaignDetails?.campaign?.goal === 'conversion' && campaignDetails?.campaign?.tier && campaignDetails?.campaign?.tier !== 'basic' &&
                                    <div className="form-group mt-4">
                                        <label htmlFor="dm-automation" className="fs-14px">{properties.dm_automation_required?.label}</label>
                                        <span className="field-desc">Choose whether you want to enable automatic direct message (DM) replies when users interact with your campaign content.</span>
                                        <RadioTileGroup
                                            options={[
                                                { id: 'y', label: 'Yes', info: 'Set DM Automation' },
                                                { id: 'n', label: 'No', info: "Don't set DM Automation" },
                                            ]}
                                            disabled={viewOnly}
                                            name="dm-automation"
                                            defaultValue={milestone?.specifications?.dm_automation_required ? 'y' : 'n'}
                                            onChange={(val) => {
                                                handleMilestoneChange('specifications', { ...milestone.specifications, dm_automation_required: val === 'y' })
                                            }}
                                        />
                                    </div>
                                }
                                {
                                    milestone?.specifications?.dm_automation_required &&
                                    <Row>
                                        <Col lg={12}>
                                            <div className="form-group mt-4">
                                                <label htmlFor="dm-automation-message" className="fs-14px">{properties.dm_automation_triggers.label} <span className="text-danger">*</span></label>
                                                <span className="field-desc">Choose user comments that will trigger DM automation.</span>
                                                <CustomMultiSelect
                                                    placeholder="Select DM Automation Triggers"
                                                    options={milestonesData?.dm_automation_trigger_list?.map((trigger: string) => ({ label: trigger, value: trigger })) || []}
                                                    values={(milestone.specifications?.dm_automation_triggers ?? [])}
                                                    onChange={(triggers) => {
                                                        const specifications = { ...milestone.specifications, dm_automation_triggers: triggers };
                                                        handleMilestoneChange('specifications', specifications);
                                                    }}
                                                    disabled={viewOnly}
                                                />
                                                {
                                                    errors.dm_automation_triggers &&
                                                    <span className="text-danger d-block" id='error-dm_automation_triggers'>{errors.dm_automation_triggers}</span>
                                                }
                                            </div>
                                        </Col>
                                        <Col lg={12}>
                                            <div className="form-group mt-4">
                                                <label htmlFor="dm-automation-message" className="fs-14px">{properties.dm_automation_button_text.label} <span className="text-danger">*</span></label>
                                                <span className="field-desc">Enter the button label that will appear in the automated DM message sent to users.</span>
                                                <Select
                                                    options={milestonesData?.dm_automation_button_text_list?.map((text: string) => ({ label: text, value: text })) || []}
                                                    value={{ label: milestone.specifications?.dm_automation_button_text ?? "", value: milestone.specifications?.dm_automation_button_text ?? "" }}
                                                    onChange={(option: any) => {
                                                        const specifications = { ...milestone.specifications, dm_automation_button_text: option?.value ?? "" };
                                                        handleMilestoneChange('specifications', specifications);
                                                    }}
                                                    isClearable
                                                    isDisabled={viewOnly}
                                                    placeholder="Select DM Automation Button Text"
                                                />
                                                {
                                                    errors.dm_automation_button_text &&
                                                    <span className="text-danger d-block" id='error-dm_automation_button_text'>{errors.dm_automation_button_text}</span>
                                                }
                                            </div>
                                        </Col>
                                    </Row>
                                }
                                <button onClick={() => showAdvanceOptions(!advanceOptions)} className="advance-filter-button mt-4" type="button">
                                    <div className='d-flex align-items-center gap-2'>
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1.99974 1H11.9999C12.5521 1 12.9998 1.44764 12.9999 1.99987L12.9999 2.58569C13 2.85097 12.8946 3.10538 12.707 3.29295L8.62599 7.37377C8.43844 7.56131 8.33307 7.81567 8.33307 8.0809L8.33307 11.7192C8.33307 12.3698 7.72168 12.8472 7.09054 12.6894L6.42387 12.5227C5.9787 12.4114 5.66641 12.0114 5.66641 11.5526L5.66641 8.08088C5.66641 7.81566 5.56105 7.56131 5.37351 7.37377L1.29263 3.29289C1.1051 3.10536 0.999739 2.851 0.999739 2.58579V2C0.999739 1.44772 1.44745 1 1.99974 1Z" stroke="black" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                        <span>Advanced</span>
                                    </div>
                                    {
                                        advanceOptions ? <i className="ri-arrow-up-s-line"></i> : <i className="ri-arrow-down-s-line"></i>
                                    }
                                </button>
                                <Collapse isOpened={advanceOptions}>
                                    <div className="form-group mt-4">
                                        <label htmlFor="references" className="fs-14px">Talking Points <span className="optional">(Optional)</span></label>
                                        <span className="field-desc mb-2">Add key points for the Creator to discuss.</span>
                                        <TalkingPoints disabled={viewOnly} talkingPoints={milestone.specifications?.talking_points ?? []} onChange={(newTalkingPoints) => {
                                            const specifications = { ...milestone.specifications, talking_points: newTalkingPoints }
                                            handleMilestoneChange('specifications', specifications);
                                        }} />
                                        {errors.talking_points &&
                                            <span className="text-danger d-block" id='error-talking-points'>{errors.talking_points}</span>
                                        }
                                    </div>
                                    <div className="form-group mt-4">
                                        <label htmlFor="references" className="fs-14px">Do(s) <span className="optional">(Optional)</span></label>
                                        <span className="field-desc">Add important things for the Creator to follow.</span>
                                        <RichListEditor disabled={viewOnly} value={milestone?.specifications?.do ?? []} onChange={(items) => {
                                            const specifications = { ...milestone.specifications };
                                            specifications.do = items;
                                            handleMilestoneChange('specifications', specifications);
                                        }} />
                                    </div>
                                    <div className="form-group mt-4">
                                        <label htmlFor="references" className="fs-14px">Must Have(s) <span className="optional">(Optional)</span></label>
                                        <span className="field-desc">Add must-have items for the Creator to include.</span>
                                        <RichListEditor disabled={viewOnly} value={milestone?.specifications?.must_have ?? []} onChange={(items) => {
                                            const specifications = { ...milestone.specifications };
                                            specifications.must_have = items;
                                            handleMilestoneChange('specifications', specifications);
                                        }} />
                                    </div>
                                    {
                                        milestoneGigType?.slug === 'repost' ? (
                                            <>
                                                <div className="form-group mt-4">
                                                    <label htmlFor="references" className="fs-14px">Sample Captions <span className="optional">(Optional)</span></label>
                                                    <span className="field-desc">Provide sample captions for the Creator to use.</span>
                                                    <RadioTileGroup
                                                        disabled={viewOnly}
                                                        name="allow-sample-captions"
                                                        options={[{ label: 'Yes', id: 'y' }, { label: 'No', id: 'n' }]}
                                                        defaultValue={sampleCaptionsAllowed ? 'y' : 'n'}
                                                        onChange={(val) => {
                                                            allowSampleCaptions(val === 'y')
                                                        }}
                                                    />
                                                </div>
                                                {
                                                    sampleCaptionsAllowed &&
                                                    <div className="d-flex flex-column gap-3 mt-3">
                                                        {
                                                            (milestone?.specifications?.sample_captions ?? []).map((caption: string, i: number) => (
                                                                <MilestoneCaption
                                                                    key={i}
                                                                    disabled={viewOnly}
                                                                    caption={caption}
                                                                    onChange={(newScript) => {
                                                                        const captions = [...(milestone.specifications?.sample_captions || [])];
                                                                        captions[i] = newScript;
                                                                        handleMilestoneChange('specifications', { ...milestone.specifications, sample_captions: captions });
                                                                    }}
                                                                    onDelete={() => {
                                                                        const captions = (milestone.specifications?.sample_captions || []).filter((_: string, idx: number) => idx !== i);
                                                                        handleMilestoneChange('specifications', { ...milestone.specifications, sample_captions: captions });
                                                                    }}
                                                                    captionName={`Caption ${i + 1}`}
                                                                />
                                                            ))

                                                        }
                                                        {
                                                            !viewOnly &&
                                                            <button type="button" className="unset-all add-more-btn" onClick={() => {
                                                                const captions = [...(milestone.specifications?.sample_captions || [])];
                                                                captions.push('');
                                                                handleMilestoneChange('specifications', { ...milestone.specifications, sample_captions: captions });

                                                            }}>
                                                                <span>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                                        <path d="M10.6243 7.50033C10.6243 7.15515 10.3445 6.87533 9.99935 6.87533C9.65417 6.87533 9.37435 7.15515 9.37435 7.50033L9.37435 9.37535H7.49935C7.15417 9.37535 6.87435 9.65517 6.87435 10.0003C6.87435 10.3455 7.15417 10.6253 7.49935 10.6253H9.37435V12.5003C9.37435 12.8455 9.65417 13.1253 9.99935 13.1253C10.3445 13.1253 10.6243 12.8455 10.6243 12.5003L10.6243 10.6253H12.4993C12.8445 10.6253 13.1243 10.3455 13.1243 10.0003C13.1243 9.65517 12.8445 9.37535 12.4993 9.37535H10.6243V7.50033Z" fill="#007AFF" />
                                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.99935 1.04199C5.0518 1.04199 1.04102 5.05277 1.04102 10.0003C1.04102 14.9479 5.0518 18.9587 9.99935 18.9587C14.9469 18.9587 18.9577 14.9479 18.9577 10.0003C18.9577 5.05277 14.9469 1.04199 9.99935 1.04199ZM2.29102 10.0003C2.29102 5.74313 5.74215 2.29199 9.99935 2.29199C14.2565 2.29199 17.7077 5.74313 17.7077 10.0003C17.7077 14.2575 14.2565 17.7087 9.99935 17.7087C5.74215 17.7087 2.29102 14.2575 2.29102 10.0003Z" fill="#007AFF" />
                                                                    </svg>
                                                                </span>
                                                                Add More Captions
                                                            </button>
                                                        }
                                                    </div>
                                                }
                                            </>

                                        ) : (
                                            <>
                                                <div className="form-group mt-4">
                                                    <label htmlFor="references" className="fs-14px">Sample Scripts <span className="optional">(Optional)</span></label>
                                                    <span className="field-desc">Provide sample scripts for the Creator to follow.</span>
                                                    <RadioTileGroup
                                                        disabled={viewOnly}
                                                        name="allow-sample-scripts"
                                                        options={[{ label: 'Yes', id: 'y' }, { label: 'No', id: 'n' }]}
                                                        defaultValue={sampleScriptsAllowed ? 'y' : 'n'}
                                                        onChange={(val) => {
                                                            allowSampleScripts(val === 'y')
                                                        }}
                                                    />
                                                </div>
                                                {
                                                    sampleScriptsAllowed &&
                                                    <div className="d-flex flex-column gap-3 mt-3">
                                                        {
                                                            (milestone?.specifications?.sample_scripts ?? []).map((script: string, i: number) => (
                                                                <MilestoneScript
                                                                    key={i}
                                                                    disabled={viewOnly}
                                                                    script={script}
                                                                    onChange={(newScript) => {
                                                                        const scripts = [...(milestone.specifications?.sample_scripts || [])];
                                                                        scripts[i] = newScript;
                                                                        handleMilestoneChange('specifications', { ...milestone.specifications, sample_scripts: scripts });
                                                                    }}
                                                                    onDelete={() => {
                                                                        const scripts = (milestone.specifications?.sample_scripts || []).filter((_: string, idx: number) => idx !== i);
                                                                        handleMilestoneChange('specifications', { ...milestone.specifications, sample_scripts: scripts });
                                                                    }}
                                                                    scriptName={`Script ${i + 1}`}
                                                                />
                                                            ))

                                                        }
                                                        {
                                                            !viewOnly &&
                                                            <button type="button" className="unset-all add-more-btn" onClick={() => {
                                                                const scripts = [...(milestone.specifications?.sample_scripts || [])];
                                                                scripts.push('');
                                                                handleMilestoneChange('specifications', { ...milestone.specifications, sample_scripts: scripts });

                                                            }}>
                                                                <span>
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                                        <path d="M10.6243 7.50033C10.6243 7.15515 10.3445 6.87533 9.99935 6.87533C9.65417 6.87533 9.37435 7.15515 9.37435 7.50033L9.37435 9.37535H7.49935C7.15417 9.37535 6.87435 9.65517 6.87435 10.0003C6.87435 10.3455 7.15417 10.6253 7.49935 10.6253H9.37435V12.5003C9.37435 12.8455 9.65417 13.1253 9.99935 13.1253C10.3445 13.1253 10.6243 12.8455 10.6243 12.5003L10.6243 10.6253H12.4993C12.8445 10.6253 13.1243 10.3455 13.1243 10.0003C13.1243 9.65517 12.8445 9.37535 12.4993 9.37535H10.6243V7.50033Z" fill="#007AFF" />
                                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.99935 1.04199C5.0518 1.04199 1.04102 5.05277 1.04102 10.0003C1.04102 14.9479 5.0518 18.9587 9.99935 18.9587C14.9469 18.9587 18.9577 14.9479 18.9577 10.0003C18.9577 5.05277 14.9469 1.04199 9.99935 1.04199ZM2.29102 10.0003C2.29102 5.74313 5.74215 2.29199 9.99935 2.29199C14.2565 2.29199 17.7077 5.74313 17.7077 10.0003C17.7077 14.2575 14.2565 17.7087 9.99935 17.7087C5.74215 17.7087 2.29102 14.2575 2.29102 10.0003Z" fill="#007AFF" />
                                                                    </svg>
                                                                </span>
                                                                Add More Scripts
                                                            </button>
                                                        }

                                                    </div>
                                                }
                                            </>

                                        )
                                    }
                                </Collapse>
                                <div className="divider my-5"></div>
                                <div className="form-group">
                                    <label htmlFor="references" className="fs-14px">Reference <span className="optional">(Optional)</span></label>
                                    <span className="field-desc">Provide examples of similar content that you like.</span>
                                    <div>
                                        {
                                            !viewOnly &&
                                            <div {...getRootPropsForSamplePosts()} className="">
                                                <div className="d-flex gap-2">

                                                    <input {...getInputPropsForSamplePost()} id="creative-input" />
                                                    <div
                                                        className="campaign-sample-post-action-btn"
                                                    >
                                                        <button type='button' className="unset-all d-flex align-items-center gap-1">
                                                            <span>
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                                    <path d="M11.25 9C11.25 8.58579 11.5858 8.25 12 8.25C12.4142 8.25 12.75 8.58579 12.75 9L12.75 11.25H15C15.4142 11.25 15.75 11.5858 15.75 12C15.75 12.4142 15.4142 12.75 15 12.75H12.75V15C12.75 15.4142 12.4142 15.75 12 15.75C11.5858 15.75 11.25 15.4142 11.25 15L11.25 12.75H9C8.58579 12.75 8.25 12.4142 8.25 12C8.25 11.5858 8.58579 11.25 9 11.25H11.25V9Z" fill="#007AFF" />
                                                                    <path fillRule="evenodd" clipRule="evenodd" d="M12 1.25C17.9371 1.25 22.75 6.06294 22.75 12C22.75 17.9371 17.9371 22.75 12 22.75C6.06294 22.75 1.25 17.9371 1.25 12C1.25 6.06294 6.06294 1.25 12 1.25ZM21.25 12C21.25 6.89137 17.1086 2.75 12 2.75C6.89137 2.75 2.75 6.89137 2.75 12C2.75 17.1086 6.89137 21.25 12 21.25C17.1086 21.25 21.25 17.1086 21.25 12Z" fill="#007AFF" />
                                                                </svg>
                                                            </span>
                                                            <span>Upload PNG, MP4, HEIC</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                        <div className="gallery-container mt-3">

                                            {getSamplePosts().length > 0 &&
                                                getSamplePosts().map((image) => {
                                                    if (getMediaTypeFromURL(image.url) === 'image') {
                                                        return (<div onClick={(e) => {
                                                            e.stopPropagation()
                                                            setSelectedMedia(image);
                                                        }} key={image.uuid} className="campaign-creative-img" style={{ backgroundImage: `url(${image.url})` }}>
                                                            {
                                                                !viewOnly &&
                                                                <button type='button' className="creative-delete-btn" onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    deleteMilestoneFile(image.uuid)
                                                                }}>
                                                                    <i className="bx bxs-trash-alt"></i>
                                                                </button>
                                                            }
                                                        </div>)
                                                    } else if (getMediaTypeFromURL(image.url) === 'video') {
                                                        return (
                                                            <div onClick={(e) => {
                                                                e.stopPropagation()
                                                                setSelectedMedia(image);
                                                            }} key={image.uuid} className="campaign-sample-post">
                                                                <video className="media" src={image.url} />
                                                                {
                                                                    !viewOnly &&
                                                                    <button type='button' className="creative-delete-btn" onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        deleteMilestoneFile(image.uuid)
                                                                    }}>
                                                                        <i className="bx bxs-trash-alt"></i>
                                                                    </button>
                                                                }
                                                                <button type='button' className="creative-play-btn">
                                                                    <i className="bx bx-play fs-3"></i>
                                                                </button>
                                                            </div>
                                                        )
                                                    }

                                                })}
                                        </div>
                                    </div>
                                </div>
                            </>
                        }
                    </div>
                }

            </CampaignCreationLayout>
            <Modal isOpen={!!selectedMedia} toggle={() => setSelectedMedia(null)}>
                <CloseButton top='0' onClose={() => setSelectedMedia(null)} />
                <ModalBody className='p-0 overflow-hidden' style={{ borderRadius: '6px', height: '75vh' }}>
                    {
                        selectedMedia &&
                        <div className='w-100 h-100 d-flex justify-content-center align-items-center'>
                            {
                                getMediaTypeFromURL(selectedMedia.url) === 'video' &&
                                <video src={selectedMedia.url} autoPlay controls className='h-100 w-100 bgc-content' />
                            }
                            {
                                getMediaTypeFromURL(selectedMedia.url) === 'image' &&
                                <div className="h-100 w-100 bg-media bgc-content" style={{
                                    backgroundImage: `url(${selectedMedia.url})`
                                }}></div>
                            }
                            <div className="media-action">
                                <span className='text-white fs-5' onClick={() => setSelectedMedia(null)}><i className="fa-solid fa-circle-xmark"></i></span>
                            </div>
                        </div>
                    }
                </ModalBody>
            </Modal>
        </>

    )
}

export default withAccessControl(CampaignDeliverables, PRIVILEGE.CAMPAIGN.CAMPAIGN.EDIT)
